import React, { Suspense } from 'react';
import { useDarkClasses } from '../../../../hooks/useDarkClasses';
import { THRILLER_MYSTERY_MODULE_DATA } from '../genreModuleData';
import { SearchableChipField, SingleSelectField } from './shared';
const ACCENT = 'bg-amber-100 text-amber-700 border-amber-500 dark:bg-amber-900/30 dark:text-amber-300';
// ═══════════════════════════════════════════════════
// FULL THRILLER / MYSTERY MODULE
// ═══════════════════════════════════════════════════
interface ThrillerMysteryModuleProps {
  subtype: string;
  setSubtype: (v: string) => void;
  investigationType: string;
  setInvestigationType: (v: string) => void;
  threatTypes: string[];
  setThreatTypes: React.Dispatch<React.SetStateAction<string[]>>;
  storyEngines: string[];
  setStoryEngines: React.Dispatch<React.SetStateAction<string[]>>;
  suspenseIntensity: string;
  setSuspenseIntensity: (v: string) => void;
}
export const ThrillerMysteryModule: React.FC<ThrillerMysteryModuleProps> = ({
  subtype,
  setSubtype,
  investigationType,
  setInvestigationType,
  threatTypes,
  setThreatTypes,
  storyEngines,
  setStoryEngines,
  suspenseIntensity,
  setSuspenseIntensity
}) => {
  const dk = useDarkClasses();
  const toggleThreat = (t: string) => setThreatTypes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  const toggleEngine = (e: string) => setStoryEngines(prev => prev.includes(e) ? prev.filter(x => x !== e) : [...prev, e]);
  return <div className="space-y-8" data-id="element-21420" data-mp="2pkn6sd6soild">
      {/* Subtype */}
      <SingleSelectField label="Subtype" placeholder="e.g. Psychological Thriller, Cozy Mystery..." items={THRILLER_MYSTERY_MODULE_DATA.subtypes} value={subtype} onChange={setSubtype} accentClass={ACCENT} data-id="element-21421" data-mp="2plybqgbz7we2" />

      {/* Investigation Type */}
      <SingleSelectField label="Investigation Type" placeholder="e.g. Police Procedural, Amateur Sleuth..." items={THRILLER_MYSTERY_MODULE_DATA.investigationTypes} value={investigationType} onChange={setInvestigationType} accentClass={ACCENT} data-id="element-21422" data-mp="2aufvqij4a1up" />

      {/* Threat Type */}
      <SearchableChipField label="Threat Type" placeholder="e.g. Serial Killer, Conspiracy, Supernatural..." items={THRILLER_MYSTERY_MODULE_DATA.threatTypes} selected={threatTypes} onToggle={toggleThreat} accentClass={ACCENT} data-id="element-21423" data-mp="eqjanxlyzxx1" />

      {/* Story Engine */}
      <SearchableChipField label="Story Engine" placeholder="e.g. Whodunit, Race Against Time..." items={THRILLER_MYSTERY_MODULE_DATA.storyEngines} selected={storyEngines} onToggle={toggleEngine} accentClass={ACCENT} data-id="element-21424" data-mp="3n2e1kdzb4e8o" />

      {/* Suspense Intensity */}
      <div data-id="element-21425" data-mp="1c805qvh1oltj">
        <label className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-21426" data-mp="1c89uygu9moy6">
          Suspense Intensity
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-21427" data-mp="1g8k2hxxphnf5">
          {THRILLER_MYSTERY_MODULE_DATA.suspenseIntensities.map(si => {
          const isSelected = suspenseIntensity === si.level;
          return <button key={si.level} onClick={() => setSuspenseIntensity(isSelected ? '' : si.level)} className={`p-3 rounded-xl border text-left transition-all
                  ${isSelected ? 'bg-amber-600 text-white border-amber-600 dark:bg-amber-400 dark:text-amber-950' : `${dk.surfaceMuted} ${dk.border} hover:border-amber-400`}`} data-id="element-21428" data-mp="2ws58nz22rxxq">
                <div className={`font-bold text-sm mb-1 ${isSelected ? '' : dk.textPrimary}`} data-id="element-21429" data-mp="2d45zgsn5syuf">
                  {si.level}
                </div>
                <div className={`text-xs ${isSelected ? 'opacity-90' : dk.textMuted}`} data-id="element-21430" data-mp="242y3wic12c41">
                  {si.description}
                </div>
              </button>;
        })}
        </div>
      </div>
    </div>;
};
// ═══════════════════════════════════════════════════
// COMPACT CROSSOVER
// ═══════════════════════════════════════════════════
interface ThrillerMysteryModuleCompactProps {
  subtype: string;
  setSubtype: (v: string) => void;
  threatTypes: string[];
  setThreatTypes: React.Dispatch<React.SetStateAction<string[]>>;
  suspenseIntensity: string;
  setSuspenseIntensity: (v: string) => void;
}
export const ThrillerMysteryModuleCompact: React.FC<ThrillerMysteryModuleCompactProps> = ({
  subtype,
  setSubtype,
  threatTypes,
  setThreatTypes,
  suspenseIntensity,
  setSuspenseIntensity
}) => {
  const dk = useDarkClasses();
  const toggleThreat = (t: string) => setThreatTypes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  return <div className="space-y-5" data-id="element-21431" data-mp="1tfer4lpk9dui">
      <SingleSelectField label="Subtype" placeholder="Search subtypes..." items={THRILLER_MYSTERY_MODULE_DATA.subtypes} value={subtype} onChange={setSubtype} accentClass={ACCENT} data-id="element-21432" data-mp="1tgg0mlqxhsa4" />

      <SearchableChipField label="Threat Type" placeholder="Search threats..." items={THRILLER_MYSTERY_MODULE_DATA.threatTypes} selected={threatTypes} onToggle={toggleThreat} accentClass={ACCENT} data-id="element-21433" data-mp="1euiqve3leim3" />

      <div data-id="element-21434" data-mp="1jqhhl8ce8cpo">
        <label className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-21435" data-mp="1jqpydbbn0265">
          Suspense Intensity
        </label>
        <div className="grid grid-cols-2 gap-2" data-id="element-21436" data-mp="1xp7furay8ifm">
          {THRILLER_MYSTERY_MODULE_DATA.suspenseIntensities.map(si => {
          const isSelected = suspenseIntensity === si.level;
          return <button key={si.level} onClick={() => setSuspenseIntensity(isSelected ? '' : si.level)} className={`p-2.5 rounded-xl border text-left transition-all
                  ${isSelected ? 'bg-amber-600 text-white border-amber-600 dark:bg-amber-400 dark:text-amber-950' : `${dk.surfaceMuted} ${dk.border}`}`} data-id="element-21437" data-mp="27ixzniw4s4oz">
                <div className={`font-bold text-xs mb-0.5 ${isSelected ? '' : dk.textPrimary}`} data-id="element-21438" data-mp="2jl7p1xmtz17y">
                  {si.level}
                </div>
                <div className={`text-[10px] ${isSelected ? 'opacity-90' : dk.textMuted}`} data-id="element-21439" data-mp="2tfznt4s5fu4b">
                  {si.description}
                </div>
              </button>;
        })}
        </div>
      </div>
    </div>;
};